import { z } from 'zod';
/**
 * Contrato de error de la API. Uno solo, para todos los endpoints.
 *
 * Toda respuesta de error tiene la misma forma: un **código estable** que el
 * cliente puede comparar, y —cuando el problema es de validación— un detalle por
 * campo con nuestro propio vocabulario.
 *
 * Lo que **nunca** viaja: el `ZodError` serializado. Es lo que hacía el
 * validador por defecto, y traía tres problemas de una: rompía el contrato
 * `{ "error": … }` que el resto de la API respeta, exponía la forma interna del
 * schema (rutas, mínimos, uniones discriminadas), y dejaba al cliente sin nada
 * estable que leer, porque los mensajes de Zod cambian entre versiones.
 *
 * Tampoco viaja el **valor rechazado**. Un mensaje de validación es un lugar
 * clásico donde termina filtrándose el dato que se estaba validando, y acá ese
 * dato puede ser un DNI o un teléfono (`pii-handling.md` §2).
 */
/** Qué le pasa a un campo. Vocabulario nuestro, corto y estable. */
export const problemaDeCampoSchema = z.enum([
    'requerido',
    'tipo_invalido',
    'formato_invalido',
    'muy_corto',
    'muy_largo',
    'valor_no_permitido',
    'campo_desconocido',
    'invalido',
]);
export const detalleDeCampoSchema = z.object({
    /** Ruta del campo, con puntos: `paciente.celular`. */
    campo: z.string().min(1),
    problema: problemaDeCampoSchema,
});
/**
 * Códigos de error de nivel de request.
 *
 * La lista es corta a propósito: un código por situación que el cliente pueda
 * manejar distinto. Los de autenticación son deliberadamente indistinguibles
 * entre sí (`auth.ts`), y ésta no los amplía.
 */
export const codigoErrorSchema = z.enum([
    'datos_invalidos',
    'no_autenticado',
    'sin_permiso',
    'no_encontrado',
    'conflicto',
    'demasiados_intentos',
    'error_interno',
]);
export const respuestaDeErrorSchema = z.object({
    error: z.string().min(1),
    /** Sólo en errores de validación. Ausente en el resto. */
    detalles: z.array(detalleDeCampoSchema).optional(),
});
/**
 * Traduce un issue de Zod a nuestro vocabulario.
 *
 * La traducción existe para que el contrato **no dependa de Zod**: si mañana
 * cambia de versión, o se reemplaza la librería, los clientes siguen leyendo los
 * mismos códigos. Es la misma razón por la que el semáforo no se guarda en FHIR:
 * lo que otro sistema consume no puede ser un detalle de implementación nuestro.
 */
export function problemaDesdeZod(issue, faltaElCampo = false) {
    switch (issue.code) {
        case 'invalid_type':
            // Zod 4 usa el mismo código para "faltó" y para "vino de otro tipo", y no
            // expone el input en el issue. La diferencia le importa a un formulario,
            // así que se resuelve mirando la entrada — nunca leyendo el mensaje de la
            // librería, que es lo que este contrato justamente no quiere depender.
            return faltaElCampo ? 'requerido' : 'tipo_invalido';
        case 'too_small':
            return 'muy_corto';
        case 'too_big':
            return 'muy_largo';
        case 'invalid_format':
        case 'invalid_string':
        case 'custom':
            return 'formato_invalido';
        case 'invalid_value':
        case 'invalid_enum_value':
        case 'invalid_union':
        case 'invalid_union_discriminator':
            return 'valor_no_permitido';
        case 'unrecognized_keys':
            return 'campo_desconocido';
        default:
            return 'invalido';
    }
}
/** Busca el valor que había en esa ruta. Sólo se usa para saber si estaba. */
function valorEnLaRuta(entrada, ruta) {
    let actual = entrada;
    for (const paso of ruta) {
        if (actual === null || typeof actual !== 'object')
            return undefined;
        actual = actual[paso];
    }
    return actual;
}
/**
 * Arma el detalle por campo a partir de los issues.
 *
 * Recibe la entrada cruda para poder distinguir un campo que falta de uno con el
 * tipo equivocado. **Del valor sólo se mira si es `undefined`**: no se copia, no
 * se compara con nada y no llega a la respuesta.
 */
export function detallesDesdeZod(issues, entrada) {
    return issues.map((issue) => ({
        campo: issue.path.length > 0 ? issue.path.map(String).join('.') : '(cuerpo)',
        problema: problemaDesdeZod(issue, valorEnLaRuta(entrada, issue.path) === undefined),
    }));
}
//# sourceMappingURL=errores.js.map